import { createWeakMapSelector } from 'app-components/utils/core/reselect';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector, weakMapMemoize } from 'reselect';

import { getLogger } from '../../utils/core';
import { numberUtils } from '../../utils/number';
import { accountsSelectors } from '../accounts';
import { transactionsSelectors } from '../transactions';

import { EPOCH_ISO } from '../../utils/date';
import { balanceSelectors } from '../calculated-balances';
import { featureFlagsSelectors } from '../feature-flags';
import { REDUCER_KEY } from './goalsReducer';
import { mkGoalFundingAccount } from './goalsTypes';

const log = getLogger('flux/goals/goalsSelectors.js');

export const getGoalsStore = (state) => state[REDUCER_KEY];
export const getResourcesById = (state) => getGoalsStore(state).resourcesById;
export const getGoalsById = (state) => getGoalsStore(state).resourcesById; // deprecated - use getResourcesById
export const getGoalById = (state, id) => getGoalsStore(state).resourcesById.get(id);
export const getIsLoading = (state) => getGoalsStore(state).isLoading;
export const getGoalsIsLoading = (state) => getGoalsStore(state).isLoading; // deprecated - use getIsLoading
export const getLoadPending = (state) => getGoalsStore(state).loadPending;
export const getGoalsLoadPending = (state) => getGoalsStore(state).loadPending; // deprecated - use getLoadPending
export const getLastSyncDate = (state) => getGoalsStore(state).lastSyncDate;

export const getGoalByTagId = createSelector(
  getResourcesById,
  (_, { id }) => id,
  (state, tagIdToFind) => state.find(({ tagId }) => tagId === tagIdToFind),
);

export const getGoalsSorted = createSelector(getGoalsById, (allGoals) =>
  allGoals.sort((a, b) => {
    const dateA = DateTime.fromISO(a.modifiedAt);
    const dateB = DateTime.fromISO(b.modifiedAt);
    if (a.modifiedAt) return dateA.diff(dateB) > 0 ? -1 : 1;
    return -1;
  }),
);
export const getGoalsSortedByDateThenCompleted = createSelector(getGoalsSorted, (sortedGoals) =>
  sortedGoals.sort((goal1, goal2) => {
    if (!goal1.completedOn && !goal2.completedOn) {
      return 0;
    }
    return !goal1.completedOn ? -1 : 1;
  }),
);
export const getCompletedGoalsById = createSelector(getGoalsSorted, (goalsById) =>
  goalsById.filter((goal) => goal.completedOn && goal.completedOn !== EPOCH_ISO),
);
export const getActiveGoalsById = createSelector(getGoalsSorted, (goalsById) =>
  goalsById.filter((goal) => !goal.completedOn || goal.completedOn === EPOCH_ISO),
);

// This selector will return all goal transactions matching id & month/year
export const getGoalTxnsForIdAndDate = createCachedSelector(
  (state, props) => transactionsSelectors.getTransactionsByAccountId(state).get(props.acctId),
  (state, props) => props.acctId,
  (_, props) => props.month,
  (_, props) => props.year,
  (goalTxns, acctIdProp, month, year) => {
    if (goalTxns?.size > 0) {
      return goalTxns.filter((txn) => {
        // filter goal txns by year & month (passed as property to selector)
        const date = DateTime.fromISO(txn.postedOn);
        return date.month === month && date.year === year;
      });
    }
    return ImmutableList();
  },
)({
  keySelector: (state, props) => props?.acctId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

export const getGoalTotalWithoutSpentSoFar = createSelector(
  getGoalTxnsForIdAndDate,
  (state, props) => accountsSelectors.getAccountById(state, props.acctId),
  (goalTxns) => {
    // const accountCreatedAtOriginal = DateTime.fromISO(account?.createdAt).toSeconds();
    // let accountCreatedAt;
    // let count = 0;
    const total =
      goalTxns.size > 0
        ? goalTxns
            .map((txnInstance) => {
              // to bypass the fact that createdAt does not exist in redux initially

              // 'posted on' optimistically defaults to current time, so if 'created at' doesn't exist, assume txn is there optimistically
              // const txnInstanceCreateAt = DateTime.fromISO(txnInstance?.createdAt || txnInstance?.postedOn).toSeconds();
              // if within 15 seconds of creating the account - it's most likely the spent so far transaction. In the future, we will filter this out via a flag
              if (txnInstance?.subtype === 'GOAL_STARTING_AMOUNT' || txnInstance.subtype === 'GOAL_SPENT') {
                return 0;
              }
              return Number(txnInstance.amount);
            })
            .reduce((sum, amt) => sum + amt)
        : 0;
    return total;
  },
);

export const getUniqueContributors = createSelector(
  (state, props) =>
    transactionsSelectors
      .getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL })
      ?.get(props.accountId),
  (goalTxns) => {
    // Helper f'n
    const getBalance = (id) =>
      goalTxns
        .filter((txn) => txn.coa.id === id)
        .map((txn) => Number(txn.amount))
        .reduce((sum, current) => numeral(sum).add(current).value(), 0);
    const balanceMap = new Map();
    if (goalTxns?.size > 0) {
      goalTxns
        .map((txn) => txn.coa.id)
        .toSet()
        .forEach((uid) => {
          const balance = getBalance(uid);
          if (balance > 0 || balance < 0) balanceMap.set(uid, balance);
          else balanceMap.delete(uid);
        });
    }
    return ImmutableMap(balanceMap);
  },
);
// NOTE: isBankAccount returns active account only, when we conslidate accountsUtils, please make sure we filter out closed accounts here.
export const getPossibleContributionAccounts = createSelector(
  accountsSelectors.getAccountsById,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentAccountGoalContributions'),
  (accountsById, investmentAccountGoalContributions) =>
    accountsById == null
      ? ImmutableMap()
      : accountsById.filter((account) =>
          investmentAccountGoalContributions
            ? accountsSelectors.isBankAccount(account) || accountsSelectors.isInvestmentAccount(account)
            : accountsSelectors.isBankAccount(account),
        ),
);

export const getGoalFundingAccountsWithBalances = createSelector(
  getPossibleContributionAccounts,
  balanceSelectors.getBalancesByAccountIds,
  (fundingAccounts, accountBalances) => {
    return fundingAccounts.map((account) => {
      const { goalsBalance, availableBalance } = accountBalances.get(account.get('id'));
      return mkGoalFundingAccount({ ...account.toJS(), availableBalance, goalsBalance });
    });
  },
);

export const getAllUniqueContributorIds = createSelector(
  (state, props) =>
    transactionsSelectors
      .getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL })
      ?.get(props.accountId),
  (state) => getPossibleContributionAccounts(state),
  (goalTxns, fundingAccounts) => {
    const accountKeys = goalTxns?.keySeq().toArray();
    const matchingAccountsIds = accountKeys?.map((key) => {
      const matchingAccount = fundingAccounts?.find((account) => account?.id === goalTxns?.getIn([key, 'coa'])?.id);
      return matchingAccount?.id ?? null;
    });
    return [...new Set(matchingAccountsIds)];
  },
);

export const getAccountsBreakdown = createSelector(
  (state) => getGoalsLoadPending(state),
  (state) =>
    transactionsSelectors.getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.GOALS }),
  (state) => getPossibleContributionAccounts(state),
  (loadPending, txnsByAccountId, accountsById) =>
    loadPending
      ? null
      : ImmutableMap().withMutations((mutableParent) => {
          txnsByAccountId.forEach((txns, accountId) => {
            if (accountsById.get(accountId)) {
              // for each account, bucketize txn amounts by goal account
              let goalBuckets = ImmutableMap().withMutations((mutableChild) => {
                txns.forEach((txn) => {
                  if (!mutableChild.has(txn.coa?.id)) {
                    // create 'bucket' if not there yet
                    mutableChild.set(txn?.coa?.id, Number(txn.amount));
                  } else {
                    mutableChild.update(txn.coa?.id, (value) => numberUtils.round(value + Number(txn.amount)));
                  }
                });
              });
              goalBuckets = goalBuckets.filter((child) => Math.abs(child) > 0); // prune 0 values
              if (goalBuckets.size > 0) {
                mutableParent.set(accountId, goalBuckets);
              }
            }
          });
        }),
);

const getImmutableListFromIds = weakMapMemoize((ids) => ImmutableList(ids));

export const getGoalsByIds = createWeakMapSelector(
  [getGoalsById, (_, ids) => (ImmutableList.isList(ids) ? ids : getImmutableListFromIds(ids))],
  (goalsById, ids) =>
    ids
      .map((id) => {
        const foundGoal = goalsById.get(id);
        if (!foundGoal) {
          log.warn(`Goal with id ${id} not found`);
          return undefined;
        }
        return foundGoal;
      })
      .filter(Boolean), // ImmutableList<Goal>
);
